import React from 'react';
import axios from 'axios';
import ProfileIcon from './ProfileIcon';
import { useThemeMode } from './ThemeModeContext';
import { getStoredProfileState, persistProfileState } from './profileIconUtils';
import { serverDomain } from '../pages/config';

const PROFILE_DIALOG_Z_INDEX = 9600;
const CROP_DIALOG_Z_INDEX = 9700;
const MOBILE_BREAKPOINT = 768;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getAuthorizationHeaders = () => {
  const token = localStorage.getItem('appstar_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function CropImageDialog({ open, imageUrl, tempUploadId, onClose, onConfirm }) {
  const isDarkMode = useThemeMode().isDarkMode;
  const [isMobile, setIsMobile] = React.useState(() => (typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false));
  const [imageBounds, setImageBounds] = React.useState(null);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [submitting, setSubmitting] = React.useState(false);
  const dragRef = React.useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    updateMobile();
    window.addEventListener('resize', updateMobile);
    window.addEventListener('orientationchange', updateMobile);
    return () => {
      window.removeEventListener('resize', updateMobile);
      window.removeEventListener('orientationchange', updateMobile);
    };
  }, []);

  React.useEffect(() => {
    if (!open) {
      setImageBounds(null);
      setOffset({ x: 0, y: 0 });
    }
  }, [open, imageUrl]);

  React.useEffect(() => {
    if (!open) return undefined;

    const handlePointerMove = (event) => {
      if (!dragRef.current.active || !imageBounds) return;
      const nextX = dragRef.current.originX + (event.clientX - dragRef.current.startX);
      const nextY = dragRef.current.originY + (event.clientY - dragRef.current.startY);
      setOffset({
        x: clamp(nextX, -imageBounds.maxOffsetX, imageBounds.maxOffsetX),
        y: clamp(nextY, -imageBounds.maxOffsetY, imageBounds.maxOffsetY)
      });
    };

    const stopDragging = () => {
      dragRef.current.active = false;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDragging);
    window.addEventListener('pointercancel', stopDragging);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDragging);
      window.removeEventListener('pointercancel', stopDragging);
    };
  }, [open, imageBounds]);

  const squareSize = isMobile ? 220 : 280;
  const cardBg = isDarkMode ? '#1f1f1f' : '#ffffff';
  const textColor = isDarkMode ? '#f5f5f5' : '#111111';
  const subtleText = isDarkMode ? 'rgba(245,245,245,0.76)' : 'rgba(17,17,17,0.72)';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)';

  const handleImageLoad = (event) => {
    const image = event.currentTarget;
    const naturalWidth = image.naturalWidth || 1;
    const naturalHeight = image.naturalHeight || 1;
    const scale = Math.max(squareSize / naturalWidth, squareSize / naturalHeight);
    const displayWidth = naturalWidth * scale;
    const displayHeight = naturalHeight * scale;
    setImageBounds({
      naturalWidth,
      naturalHeight,
      displayWidth,
      displayHeight,
      scale,
      maxOffsetX: Math.max(0, (displayWidth - squareSize) / 2),
      maxOffsetY: Math.max(0, (displayHeight - squareSize) / 2)
    });
    setOffset({ x: 0, y: 0 });
  };

  const handlePointerDown = (event) => {
    if (!imageBounds || submitting) return;
    dragRef.current = {
      active: true,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y
    };
  };

  const handleConfirm = async () => {
    if (!imageBounds || submitting) return;

    const cropSize = squareSize / imageBounds.scale;
    const cropX = clamp(((imageBounds.displayWidth - squareSize) / 2 - offset.x) / imageBounds.scale, 0, imageBounds.naturalWidth - cropSize);
    const cropY = clamp(((imageBounds.displayHeight - squareSize) / 2 - offset.y) / imageBounds.scale, 0, imageBounds.naturalHeight - cropSize);

    setSubmitting(true);
    try {
      await onConfirm({
        temp_upload_id: tempUploadId,
        crop_x: Math.round(cropX),
        crop_y: Math.round(cropY),
        crop_size: Math.round(cropSize)
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.68)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? 12 : 24,
        zIndex: CROP_DIALOG_Z_INDEX
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-image-crop-title"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: isMobile ? 340 : 420,
          borderRadius: 18,
          background: cardBg,
          color: textColor,
          padding: isMobile ? 18 : 24,
          boxShadow: '0 18px 40px rgba(0,0,0,0.34)'
        }}
      >
        <h2 id="profile-image-crop-title" style={{ margin: 0, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
          Position Profile Image
        </h2>
        <p style={{ marginTop: 10, marginBottom: 16, color: subtleText, lineHeight: 1.45 }}>
          Drag the image inside the square to choose how your profile icon should be cropped.
        </p>
        <div
          onPointerDown={handlePointerDown}
          style={{
            width: squareSize,
            height: squareSize,
            margin: '0 auto',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 18,
            border: `1px solid ${borderColor}`,
            background: isDarkMode ? '#111111' : '#f1f1f1',
            touchAction: 'none',
            cursor: submitting ? 'default' : 'grab'
          }}
        >
          <img
            src={imageUrl}
            alt=""
            onLoad={handleImageLoad}
            draggable={false}
            style={imageBounds ? {
              position: 'absolute',
              width: imageBounds.displayWidth,
              height: imageBounds.displayHeight,
              left: '50%',
              top: '50%',
              transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
              userSelect: 'none',
              pointerEvents: 'none'
            } : {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.001,
              pointerEvents: 'none'
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.9), inset 0 0 0 999px rgba(0,0,0,0.12)',
              borderRadius: 18,
              pointerEvents: 'none'
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '10px 16px',
              borderRadius: 12,
              border: `1px solid ${borderColor}`,
              background: 'transparent',
              color: textColor,
              cursor: submitting ? 'default' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!imageBounds || submitting}
            style={{
              padding: '10px 16px',
              borderRadius: 12,
              border: 'none',
              background: '#003366',
              color: '#ffffff',
              fontWeight: 600,
              cursor: !imageBounds || submitting ? 'default' : 'pointer',
              opacity: !imageBounds || submitting ? 0.65 : 1
            }}
          >
            {submitting ? 'Saving...' : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProfileEditorDialog({ open, onClose }) {
  const { isDarkMode } = useThemeMode();
  const [isMobile, setIsMobile] = React.useState(() => (typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false));
  const [form, setForm] = React.useState({ username: '', firstName: '', lastName: '', email: '' });
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');
  const [cropDialog, setCropDialog] = React.useState({ open: false, tempUploadId: '', tempUrl: '' });
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    updateMobile();
    window.addEventListener('resize', updateMobile);
    window.addEventListener('orientationchange', updateMobile);
    return () => {
      window.removeEventListener('resize', updateMobile);
      window.removeEventListener('orientationchange', updateMobile);
    };
  }, []);

  React.useEffect(() => {
    if (!open) return;

    const stored = getStoredProfileState();
    setForm({
      username: stored.username || '',
      firstName: stored.firstName || '',
      lastName: stored.lastName || '',
      email: stored.email || ''
    });
    setErrorMessage('');
    setSuccessMessage('');
  }, [open]);

  React.useEffect(() => {
    if (!open || successMessage !== 'Profile updated.') return undefined;

    const closeTimer = window.setTimeout(() => {
      onClose();
    }, 2000);

    return () => {
      window.clearTimeout(closeTimer);
    };
  }, [open, onClose, successMessage]);

  const cardBg = isDarkMode ? '#1f1f1f' : '#ffffff';
  const pageText = isDarkMode ? '#f5f5f5' : '#111111';
  const mutedText = isDarkMode ? 'rgba(245,245,245,0.74)' : 'rgba(17,17,17,0.68)';
  const inputBg = isDarkMode ? '#111111' : '#f7f7f7';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)';

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const validate = () => {
    if (!String(form.firstName || '').trim()) return 'First name is required.';
    if (!String(form.lastName || '').trim()) return 'Last name is required.';
    if (!String(form.email || '').trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.email || '').trim())) return 'Enter a valid email address.';
    return '';
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      setSuccessMessage('');
      return;
    }

    const token = localStorage.getItem('appstar_jwt');
    if (!token) {
      setErrorMessage('Your session has expired. Please log in again.');
      setSuccessMessage('');
      return;
    }

    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const response = await axios.post(
        `${serverDomain}update-profile.php`,
        {
          first_name: String(form.firstName || '').trim(),
          last_name: String(form.lastName || '').trim(),
          email: String(form.email || '').trim()
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthorizationHeaders()
          }
        }
      );

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Unable to save profile.');
      }

      persistProfileState({
        username: form.username,
        firstName: String(form.firstName || '').trim(),
        lastName: String(form.lastName || '').trim(),
        email: String(form.email || '').trim(),
        profileIconHash: response?.data?.user?.profile_icon_hash
      });
      setSuccessMessage('Profile updated.');
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Unable to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelection = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please choose an image file.');
      setSuccessMessage('');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Profile images must be 5 MB or smaller.');
      setSuccessMessage('');
      return;
    }

    setUploading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const body = new FormData();
      body.append('profile_image', file);

      const response = await axios.post(`${serverDomain}upload-profile-icon.php`, body, {
        headers: {
          ...getAuthorizationHeaders()
        }
      });

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Unable to upload image.');
      }

      setCropDialog({
        open: true,
        tempUploadId: response.data.temp_upload_id,
        tempUrl: response.data.temp_image_url
      });
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Unable to upload image.');
    } finally {
      setUploading(false);
    }
  };

  const handleCropConfirm = async (cropPayload) => {
    try {
      const response = await axios.post(
        `${serverDomain}edit-profile-icon-position.php`,
        cropPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthorizationHeaders()
          }
        }
      );

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Unable to save profile image.');
      }

      persistProfileState({ profileIconHash: response.data.profile_icon_hash });
      setCropDialog({ open: false, tempUploadId: '', tempUrl: '' });
      setErrorMessage('');
      setSuccessMessage('Profile image updated.');
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Unable to save profile image.');
      setSuccessMessage('');
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.56)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? 12 : 24,
          zIndex: PROFILE_DIALOG_Z_INDEX
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-editor-title"
          onClick={(event) => event.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: 720,
            borderRadius: 22,
            background: cardBg,
            color: pageText,
            padding: isMobile ? 18 : 24,
            boxShadow: '0 22px 48px rgba(0,0,0,0.3)',
            maxHeight: 'min(92vh, 760px)',
            overflowY: 'auto'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <h2 id="profile-editor-title" style={{ margin: 0, fontSize: isMobile ? '1.2rem' : '1.45rem' }}>
                Edit Profile
              </h2>
              {/* <p style={{ marginTop: 8, marginBottom: 0, color: mutedText, lineHeight: 1.45 }}>
                Update your name, email address, and profile image.
              </p> */}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close profile editor"
              style={{
                border: 'none',
                background: 'transparent',
                color: mutedText,
                cursor: 'pointer',
                fontSize: '1.45rem',
                lineHeight: 1,
                padding: 0,
                flexShrink: 0,
                alignSelf: 'flex-start'
              }}
            >
              ×
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px minmax(0, 1fr)', gap: isMobile ? 20 : 28, marginTop: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'stretch' : 'center' }}>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: isMobile ? 'center' : 'center', gap: 14, width: '100%' }}>
                <div style={{ padding: 12, borderRadius: 24, background: isDarkMode ? '#151515' : '#f3f4f6', flexShrink: 0 }}>
                  <ProfileIcon size={isMobile ? 92 : 108} />
                </div>
                <div style={{ display: 'grid', gap: 6, minWidth: 0, flex: '1 1 auto', width: isMobile ? 'auto' : '100%', textAlign: isMobile ? 'left' : 'center' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: mutedText }}>
                    Username
                  </span>
                  <span style={{ color: mutedText, fontSize: '1rem', wordBreak: 'break-word' }}>
                    {form.username}
                  </span>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={uploading}
                    style={{
                      marginTop: 4,
                      padding: '10px 16px',
                      borderRadius: 12,
                      border: `1px solid ${borderColor}`,
                      background: inputBg,
                      color: pageText,
                      fontWeight: 600,
                      cursor: uploading ? 'default' : 'pointer',
                      width: isMobile ? '100%' : 'auto',
                      justifySelf: isMobile ? 'stretch' : 'center',
                      opacity: uploading ? 0.7 : 1
                    }}
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelection}
                style={{ display: 'none' }}
              />
            </div>

            <div style={{ display: 'grid', gap: 16 }}>
              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: mutedText }}>
                  First Name
                </span>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={updateField('firstName')}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: `1px solid ${borderColor}`,
                    background: inputBg,
                    color: pageText,
                    fontSize: '1rem'
                  }}
                />
              </label>
              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: mutedText }}>
                  Last Name
                </span>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={updateField('lastName')}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: `1px solid ${borderColor}`,
                    background: inputBg,
                    color: pageText,
                    fontSize: '1rem'
                  }}
                />
              </label>
              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: mutedText }}>
                  Email Address
                </span>
                <input
                  type="email"
                  value={form.email}
                  onChange={updateField('email')}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: `1px solid ${borderColor}`,
                    background: inputBg,
                    color: pageText,
                    fontSize: '1rem'
                  }}
                />
              </label>
            </div>
          </div>

          {(errorMessage || successMessage) && (
            <div
              style={{
                marginTop: 18,
                padding: '12px 14px',
                borderRadius: 12,
                background: errorMessage ? (isDarkMode ? '#4a1717' : '#fef2f2') : (isDarkMode ? '#143523' : '#ecfdf3'),
                color: errorMessage ? (isDarkMode ? '#fecaca' : '#991b1b') : (isDarkMode ? '#bbf7d0' : '#166534'),
                border: `1px solid ${errorMessage ? (isDarkMode ? '#7f1d1d' : '#fecaca') : (isDarkMode ? '#166534' : '#bbf7d0')}`
              }}
            >
              {errorMessage || successMessage}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 22, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '11px 18px',
                borderRadius: 12,
                border: `1px solid ${borderColor}`,
                background: 'transparent',
                color: pageText,
                cursor: saving ? 'default' : 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '11px 18px',
                borderRadius: 12,
                border: 'none',
                background: '#003366',
                color: '#ffffff',
                fontWeight: 700,
                cursor: saving ? 'default' : 'pointer',
                opacity: saving ? 0.72 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </div>
      </div>

      <CropImageDialog
        open={cropDialog.open}
        imageUrl={cropDialog.tempUrl}
        tempUploadId={cropDialog.tempUploadId}
        onClose={() => setCropDialog({ open: false, tempUploadId: '', tempUrl: '' })}
        onConfirm={handleCropConfirm}
      />
    </>
  );
}